import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Users, ShoppingBag, TrendingUp,
  CheckCircle, XCircle, Clock, LogOut, Home
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);

  // Simple authentication (mock)
  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth === 'authenticated') {
      setIsAuthenticated(true);
      loadBookings();
    }
  }, []);

  const loadBookings = () => {
    const storedBookings = JSON.parse(localStorage.getItem('cafeBookings') || '[]');
    setBookings(storedBookings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
  };

  const handleLogin = (e) => {
    e.preventDefault();
    // Simple password check (in production, use proper authentication)
    if (password === 'admin123') {
      localStorage.setItem('adminAuth', 'authenticated');
      setIsAuthenticated(true);
      loadBookings();
      toast.success('Welcome to Admin Dashboard!');
    } else {
      toast.error('Invalid password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setPassword('');
    toast.success('Logged out successfully');
  };

  const updateBookingStatus = (bookingId, newStatus) => {
    const updatedBookings = bookings.map(booking =>
      booking.id === bookingId ? { ...booking, status: newStatus } : booking
    );
    setBookings(updatedBookings);
    localStorage.setItem('cafeBookings', JSON.stringify(updatedBookings));
    toast.success(`Booking ${newStatus}`);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="cafe-card p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
                <img src="/favicon.png" alt="Insta Cafe Logo" className="w-full h-full object-cover" />
              </div>
              <h1 className="text-3xl font-display font-bold text-foreground mb-2">
                INSTA CAFE
              </h1>
              <p className="text-muted-foreground">Admin Dashboard</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Admin Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  required
                />
              </div>
              <Button type="submit" className="w-full btn-primary">
                Login to Dashboard
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground mb-2">Demo credentials:</p>
              <code className="text-xs bg-muted px-2 py-1 rounded">admin123</code>
            </div>

            <div className="mt-6 text-center">
              <Button variant="link" onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-2" />
                Back to Website
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard Stats
  const stats = {
    totalBookings: bookings.length,
    pendingBookings: bookings.filter(b => b.status === 'pending').length,
    confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
    rejectedBookings: bookings.filter(b => b.status === 'rejected').length
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground border-b border-primary-foreground/20">
        <div className="section-container py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-foreground/10 rounded-full flex items-center justify-center overflow-hidden">
                <img src="/favicon.png" alt="Insta Cafe Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-xl font-display font-bold">INSTA CAFE</h1>
                <p className="text-sm text-primary-foreground/80">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-2" />
                View Website
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="section-container py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Bookings</CardTitle>
              <Calendar className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalBookings}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="w-4 h-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.pendingBookings}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Confirmed</CardTitle>
              <CheckCircle className="w-4 h-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.confirmedBookings}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Rejected</CardTitle>
              <XCircle className="w-4 h-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.rejectedBookings}</div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings Table */}
        <Card>
          <CardHeader>
            <CardTitle>Booking Requests</CardTitle>
            <CardDescription>Manage customer seat reservations</CardDescription>
          </CardHeader>
          <CardContent>
            {bookings.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No bookings yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold">Customer</th>
                      <th className="text-left p-3 font-semibold">Contact</th>
                      <th className="text-left p-3 font-semibold">Date & Time</th>
                      <th className="text-left p-3 font-semibold">Guests</th>
                      <th className="text-left p-3 font-semibold">Status</th>
                      <th className="text-left p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id} className="border-b hover:bg-muted/50">
                        <td className="p-3">
                          <div className="font-medium">{booking.name}</div>
                          {booking.message && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Note: {booking.message}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-sm">{booking.phone}</td>
                        <td className="p-3 text-sm">
                          <div>{booking.date}</div>
                          <div className="text-muted-foreground">{booking.time}</div>
                        </td>
                        <td className="p-3 text-sm">{booking.guests}</td>
                        <td className="p-3">
                          {booking.status === 'pending' && (
                            <Badge variant="outline" className="bg-yellow-500/10 text-yellow-700 border-yellow-300">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                          {booking.status === 'confirmed' && (
                            <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-300">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Confirmed
                            </Badge>
                          )}
                          {booking.status === 'rejected' && (
                            <Badge variant="outline" className="bg-red-500/10 text-red-700 border-red-300">
                              <XCircle className="w-3 h-3 mr-1" />
                              Rejected
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          {booking.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => updateBookingStatus(booking.id, 'rejected')}
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                          {booking.status !== 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateBookingStatus(booking.id, 'pending')}
                            >
                              Reset
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;